// eslint-disable-next-line max-classes-per-file
import { AuthClient } from '../clients/AuthClient'
import { AccountDto } from '../gen/backendClient'

export class LocalStorageToken {
  private static TOKEN_KEY = 'token'

  public static getCurrentToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY)
  }

  public static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token)
  }

  public static removeStaticToken(): void {
    localStorage.removeItem(this.TOKEN_KEY)
  }
}

export class AuthService {
  public static account?: AccountDto = undefined

  public static async loginWithPassword(email: string, password: string): Promise<string> {
    const accessToken = await AuthClient.getAccessToken(email, password)

    LocalStorageToken.setToken(accessToken)
    await this.hydrateAccountAfterLogin()

    return accessToken
  }

  public static async adminLogin(customerId: string, password: string): Promise<string> {
    const accessToken = await AuthClient.getAdminAccessToken(customerId, password)

    LocalStorageToken.setToken(accessToken)
    await this.hydrateAccountAfterLogin()

    return accessToken
  }

  public static async tryLogInWithLocalToken(): Promise<AccountDto | null> {
    const token = LocalStorageToken.getCurrentToken()
    if (!token) {
      return null
    }

    try {
      return await this.loadAccountWithRetry()
    } catch (error) {
      console.error('Failed to restore session from local token', error)

      if (this.isAuthenticationError(error)) {
        LocalStorageToken.removeStaticToken()
      }

      this.account = undefined

      return null
    }
  }

  // public static logout(): void {
  //   localStorage.removeItem('user')
  // }

  public static isLogged(): boolean {
    return Boolean(this.account)
  }

  // public static isLoggedAsAdmin(): boolean {
  //   return !!this.account && this.account.roles.includes('forto-admin')
  // }

  public static async loadAccount(): Promise<AccountDto> {
    const responseDto = await AuthClient.getCurrentAccount()
    this.account = responseDto

    return responseDto
  }

  public static async logout(): Promise<void> {
    LocalStorageToken.removeStaticToken()
    this.account = undefined
    window.location.reload()
  }

  public static async requestPasswordReset(email: string) {
    await AuthClient.requestPasswordReset(email)
  }

  private static async hydrateAccountAfterLogin(): Promise<void> {
    try {
      await this.loadAccountWithRetry()
    } catch (error) {
      if (this.isAuthenticationError(error)) {
        LocalStorageToken.removeStaticToken()
        this.account = undefined
        throw error
      }

      console.error('Failed to load account immediately after login, deferring to app bootstrap', error)
      this.account = undefined
    }
  }

  private static async loadAccountWithRetry(maxAttempts = 12, delayMs = 500): Promise<AccountDto> {
    let lastError: unknown

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return await this.loadAccount()
      } catch (error) {
        lastError = error

        if (attempt === maxAttempts) {
          break
        }

        await this.wait(delayMs)
      }
    }

    throw lastError
  }

  private static async wait(delayMs: number): Promise<void> {
    await new Promise((resolve) => {
      window.setTimeout(resolve, delayMs)
    })
  }

  private static isAuthenticationError(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      typeof error.response === 'object' &&
      error.response !== null &&
      'status' in error.response &&
      (error.response.status === 401 || error.response.status === 403)
    )
  }
}
